// ===========================================
// Class Detail Page
// ===========================================
// View class details and manage enrollments

import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Alert,
  Skeleton,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  OpenInNew as OpenInNewIcon,
  CloudQueue as CloudIcon,
  Message as MessageIcon,
  Event as EventIcon,
  Place as PlaceIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ActionMenu, { ActionMenuItem } from '../../components/common/ActionMenu';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import {
  useLesson,
  useLessons,
  useLessonEnrollments,
  useLessonCapacity,
  useEnrollStudent,
  useBulkEnrollStudents,
  useUnenrollStudent,
  useLessonExceptions,
  useLessonClassMembers,
  useReplacementCoverage,
  useDeleteLessonException,
} from '../../hooks/useLessons';
import { useStudents, useStudentEnrollments } from '../../hooks/useUsers';
import {
  LessonEnrollment,
  getDayName,
  formatTime,
} from '../../api/lessons.api';
import FileList from '../../components/googleDrive/FileList';
import ClassLinksSection from '../../components/classLinks/ClassLinksSection';
import DriveFileUploader from '../../components/googleDrive/DriveFileUploader';
import SyncStatusBadge from '../../components/googleDrive/SyncStatusBadge';
import { useGoogleDriveAuthStatus, useFolderMappings } from '../../hooks/useGoogleDrive';
import { formatSyncTime } from '../../api/googleDrive.api';
import { useNotesByLesson } from '../../hooks/useNotes';
import {
  findRelatedTerms,
  findCurrentTermLesson,
  LessonInfoCard,
  LessonNotesCard,
  HybridPatternCard,
  OpenClinicWeeksDialog,
  EnrollmentCard,
  EnrollStudentsDialog,
  ViewStudentDialog,
  AllNotesDialog,
  LessonPiecesCard,
  AssignReplacementTeacherDialog,
  CancelClassDialog,
  RescheduleClassDialog,
  ChangeLocationDialog,
  ClassMessageModal,
} from '../../components/admin/lessonDetail';
import { useClassBroadcast } from '../../hooks/useMessages';
import { parseUTCDate } from '../../utils/dateUtils';
import { useSnackbar } from 'notistack';
import AlreadyInvoicedDialog from '../../components/invoices/AlreadyInvoicedDialog';
import { useAlreadyInvoicedPrompt } from '../../hooks/useAlreadyInvoicedPrompt';
import { useBillingNotice } from '../../hooks/useBillingNotice';
import { useTerms } from '../../hooks/useAdmin';
import { useTermLock } from '../../hooks/useTermLock';
import { termLockedReason } from '../../utils/termLock';
import { useAuth } from '../../contexts/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { isTeacherViewPath } from '../../components/layout/effectiveView';

// ===========================================
// COMPONENT
// ===========================================

export default function LessonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this is teacher view and build base path with school slug
  const schoolSlug = extractSchoolSlugFromPath(location.pathname);
  const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';
  const basePath = isTeacherViewPath(location.pathname) ? `${slugPrefix}/teacher` : `${slugPrefix}/admin`;
  const isTeacherView = basePath.endsWith('/teacher');

  const { can } = usePermissions();
  // Reopening a locked term is the owner's (`ownerOnly`): named by the school's title (slice 2).
  const ownerTitle = useOwnerTitle();
  // Replacement coverage and the exceptions list are scheduling.read routes: a plain
  // teacher login holds no permissions, so it must not ask (#272; a teacher-scoped read is #526).
  const mayReadSchedule = can('scheduling', 'read');

  // Queries
  const { data: lessonData, isLoading, error } = useLesson(id || '');
  const { data: replacementCoverage } = useReplacementCoverage(id || '', { enabled: mayReadSchedule });
  const hasReplacement =
    !!replacementCoverage && (replacementCoverage.weeks.length > 0 || !!replacementCoverage.global);
  const { data: allLessonsData } = useLessons(); // Fetch all lessons to find related terms
  const { data: enrollmentsData, isLoading: enrollmentsLoading } = useLessonEnrollments(id || '');
  const { data: capacityData } = useLessonCapacity(id || '');
  const { data: studentsData } = useStudents();
  const { data: driveAuthStatus } = useGoogleDriveAuthStatus();
  const { data: folderMappings } = useFolderMappings();
  const { data: lessonNotes, isLoading: isLoadingNotes } = useNotesByLesson(id || '');
  const { data: exceptionsData } = useLessonExceptions(id, { enabled: mayReadSchedule });

  // Check if there are cancelled occurrences

  // State for file uploader
  const [uploaderOpen, setUploaderOpen] = useState(false);

  // State for all notes modal
  const [allNotesModalOpen, setAllNotesModalOpen] = useState(false);

  // State for replacement teacher modal
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);

  // State for cancel class modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // State for reschedule class modal
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [changeLocationModalOpen, setChangeLocationModalOpen] = useState(false);

  // State for the open-clinic-weeks modal (launched from the Individual Weeks chips)
  const [openWeeksDialogOpen, setOpenWeeksDialogOpen] = useState(false);

  // State for message class modal
  const [messageModalOpen, setMessageModalOpen] = useState(false);

  // Class members for messaging (fetched lazily when modal opens)
  const {
    data: classMembersData,
    isLoading: classMembersLoading,
    isError: classMembersError,
  } = useLessonClassMembers(id || '', messageModalOpen);
  const classBroadcastMutation = useClassBroadcast();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  // Drive file upload/edit/delete: a teacher login (own uploads, enforced server-side) or integrations.write (roles plan 5.6).
  const canManageDriveFiles = isTeacherView || can('integrations', 'write');
  // Restore (un-cancel) a cancelled occurrence from the Class Details card.
  const restoreException = useDeleteLessonException();

  const handleSendClassMessage = async (recipientIds: string[], content: string) => {
    console.log('[ClassBroadcast] sending to', recipientIds.length, 'recipients');
    try {
      const result = await classBroadcastMutation.mutateAsync({
        lessonId: id || '',
        recipientIds,
        content,
      });
      console.log('[ClassBroadcast] result:', result);
      if (result.failed === 0) {
        enqueueSnackbar(`Message sent to ${result.sent} ${result.sent === 1 ? 'person' : 'people'}`, { variant: 'success' });
      } else if (result.sent > 0) {
        enqueueSnackbar(`Sent to ${result.sent}, failed for ${result.failed}`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to send messages', { variant: 'error' });
      }
      setMessageModalOpen(false);
    } catch (err) {
      console.error('[ClassBroadcast] caught error:', err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = axiosErr?.response?.data?.message || axiosErr?.message || 'Failed to send messages';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const isDriveConnected = driveAuthStatus?.isConnected ?? false;
  const rootFolderName = driveAuthStatus?.rootFolderName;

  // Find the folder mapping for this lesson
  const lessonFolderMapping = useMemo(() => {
    if (!folderMappings || !id) return null;
    return folderMappings.find((m) => m.lessonId === id) || null;
  }, [folderMappings, id]);

  const lesson = lessonData;
  const allLessons = allLessonsData ?? [];
  const enrollments = enrollmentsData ?? [];
  const capacity = capacityData;
  const allStudents = studentsData ?? [];

  // Find all terms this lesson spans (same name, teacher, room, day, time)
  const relatedTerms = useMemo(() => {
    if (!lesson) return [];
    return findRelatedTerms(lesson, allLessons);
  }, [lesson, allLessons]);

  // For term-driven actions (assign replacement, reschedule) a multi-term lesson
  // should default to the CURRENT term's row — the page may be showing a past
  // term's row. Resolves to the sibling row whose term is current, else the
  // navigated lesson (single-term or between-terms → unchanged).
  const currentTermLesson = useMemo(() => {
    if (!lesson) return lesson;
    return findCurrentTermLesson(lesson, allLessons);
  }, [lesson, allLessons]);

  // Has THIS row's term already ended? A multi-term class is one row per term;
  // week-config edits (open clinic weeks) on an ended term's row do nothing
  // for parents (they see the newer row), so the door is hidden there.
  const lessonTermEnded = useMemo(() => {
    if (!lesson?.Term?.endDate) return false;
    const end = parseUTCDate(lesson.Term.endDate);
    end.setUTCHours(23, 59, 59, 999);
    return end < new Date();
  }, [lesson]);

  // Is THIS row's term LOCKED (the next term has started, no live reopen)? Shown as a banner: the
  // server refuses class edits, enrolment changes and clinic edits on it (409 TERM_LOCKED). A term
  // that has merely ended is still editable. No lock info (and no banner) without school_setup
  // access - the server still gates.
  // Only fetched for a user who can read terms (ADMIN or `school_setup.read`): a plain teacher gets
  // a 403 from `GET /admin/terms`, so the query is not fired for them and no banner shows.
  const canReadTerms = can('school_setup', 'read');
  const { data: termsForLock } = useTerms({ enabled: canReadTerms });
  const { stateOf: termLockStateOf } = useTermLock(termsForLock);
  const lessonLockReason = useMemo(() => {
    const termId = lesson?.Term?.id ?? lesson?.termId;
    const state = termId ? termLockStateOf(termId) : undefined;
    const term = termId ? termsForLock?.find((t) => t.id === termId) : undefined;
    return state && term ? termLockedReason(term, state) : '';
  }, [lesson, termsForLock, termLockStateOf]);

  // Get students not already enrolled
  const enrolledStudentIds = new Set(enrollments.map((e) => e.Student.id));
  const availableStudents = allStudents.filter(
    (s) => !enrolledStudentIds.has(s.id) && s.isActive
  );

  // Mutations
  const enrollMutation = useEnrollStudent();
  const bulkEnrollMutation = useBulkEnrollStudents();
  const unenrollMutation = useUnenrollStudent();
  // "Already invoiced": students just enrolled into a term their family was already billed for
  // (term invoicing skips that family, so nothing bills them). Kept for the Back button.
  const alreadyInvoiced = useAlreadyInvoicedPrompt('lesson-detail-already-invoiced');
  const billingNotice = useBillingNotice();

  // Enrollment modal state
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  // Mid-term start: null = the class's own start (nothing is sent)
  const [enrollStartWeek, setEnrollStartWeek] = useState<number | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [studentSearch, setStudentSearch] = useState('');

  // Unenroll confirmation state
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  const [studentToUnenroll, setStudentToUnenroll] = useState<LessonEnrollment | null>(null);

  // View student modal state
  const [viewStudentOpen, setViewStudentOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<LessonEnrollment['Student'] | null>(null);

  // Fetch selected student's enrollments
  const { data: studentEnrollments, isLoading: isLoadingStudentEnrollments } = useStudentEnrollments(
    selectedStudent?.id || null
  );

  // Handlers
  const handleEdit = () => {
    navigate(`${basePath}/classes`, { state: { editLessonId: lesson?.id } });
  };

  const handleOpenEnrollModal = () => {
    setSelectedStudents([]);
    setEnrollStartWeek(null);
    setStudentSearch('');
    setEnrollModalOpen(true);
  };

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleEnrollStudents = async () => {
    if (!id || selectedStudents.length === 0) return;

    try {
      if (selectedStudents.length === 1) {
        const result = await enrollMutation.mutateAsync({
          lessonId: id,
          studentId: selectedStudents[0],
          startWeek: enrollStartWeek,
        });
        alreadyInvoiced.show(result.alreadyInvoiced);
        billingNotice('enrol', result.invoicesMayNeedAttention);
      } else {
        const result = await bulkEnrollMutation.mutateAsync({
          lessonId: id,
          studentIds: selectedStudents,
          startWeek: enrollStartWeek,
        });
        alreadyInvoiced.show(result.alreadyInvoiced);
        billingNotice('enrol', result.invoicesMayNeedAttention);
      }
      setEnrollModalOpen(false);
      setSelectedStudents([]);
    } catch {
      // Error handled by mutation
    }
  };

  const handleUnenroll = (enrollment: LessonEnrollment) => {
    setStudentToUnenroll(enrollment);
    setUnenrollDialogOpen(true);
  };

  const handleConfirmUnenroll = async () => {
    if (!id || !studentToUnenroll) return;

    try {
      await unenrollMutation.mutateAsync({
        lessonId: id,
        studentId: studentToUnenroll.Student.id,
      });
      setUnenrollDialogOpen(false);
      setStudentToUnenroll(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleViewStudent = (student: LessonEnrollment['Student']) => {
    setSelectedStudent(student);
    setViewStudentOpen(true);
  };

  const isEnrollLoading = enrollMutation.isPending || bulkEnrollMutation.isPending;
  const isUnenrollLoading = unenrollMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Error state
  if (error || !lesson) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ? 'Failed to load class details.' : 'Class not found.'}
        </Alert>
        <Button startIcon={<BackIcon />} component={Link} to={`${basePath}/classes`}>
          Back to Classes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={lesson.name}
        subtitle={`${lesson.hasSchedule && lesson.dayOfWeek != null ? `${getDayName(lesson.dayOfWeek)} ${formatTime(lesson.startTime!)} - ${formatTime(lesson.endTime!)}` : 'Schedule TBD'}${lesson.Room ? ` · ${lesson.Room.name}${lesson.Room.Location ? `, ${lesson.Room.Location.name}` : ''}` : ''}`}
      />

      {lessonLockReason && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {lessonLockReason} Classes, enrolments and clinic bookings in it can&apos;t be changed. Your{' '}
          {ownerTitle} can reopen it in School Setup &rarr; Terms.
        </Alert>
      )}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button startIcon={<BackIcon />} component={Link} to={`${basePath}/classes`}>
          Back
        </Button>
        {/* Primary CTA + secondary actions grouped on the right; secondary
            actions collapse into ⋮ More to keep the bar uncluttered. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {(() => {
          const canMessage =
            !isTeacherView ||
            lesson.Instructor?.User?.id === user?.id ||
            lesson.ReplacementTeacher?.User?.id === user?.id;
          return canMessage ? (
            <Button
              variant="contained"
              startIcon={<MessageIcon />}
              onClick={() => setMessageModalOpen(true)}
              sx={{
                backgroundColor: '#4580E4',
                color: 'white',
                '&:hover': { backgroundColor: '#3899ec' },
              }}
            >
              Message Class
            </Button>
          ) : null;
        })()}
        {(() => {
          const hasSchedule = lesson.hasSchedule && lesson.dayOfWeek != null;
          const menuItems: ActionMenuItem[] = [];
          if (!isTeacherView) {
            menuItems.push({
              key: 'replacement',
              label: hasReplacement ? 'Edit Replacement Teacher' : 'Assign Replacement Teacher',
              icon: <PersonIcon fontSize="small" />,
              onClick: () => setReplacementModalOpen(true),
            });
            if (hasSchedule) {
              menuItems.push({ key: 'reschedule', label: 'Reschedule Class', icon: <EventIcon fontSize="small" />, onClick: () => setRescheduleModalOpen(true) });
              menuItems.push({ key: 'location', label: 'Change Location', icon: <PlaceIcon fontSize="small" />, onClick: () => setChangeLocationModalOpen(true) });
            }
            menuItems.push({ key: 'edit', label: 'Edit Class', icon: <EditIcon fontSize="small" />, onClick: handleEdit });
            menuItems.push({
              key: 'cancel',
              label: 'Cancel Class',
              icon: <EventBusyIcon fontSize="small" />,
              onClick: () => setCancelModalOpen(true),
              // No "already cancelled" gate here any more: the dialog offers a week picker
              // with cancelled weeks disabled, so a second cancellation is a normal action.
              destructive: true,
            });
          }
          return <ActionMenu items={menuItems} ariaLabel="More class actions" />;
        })()}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Class Details Card */}
        <Grid item xs={12} md={6}>
          <LessonInfoCard
            lesson={lesson}
            relatedTerms={relatedTerms}
            exceptions={exceptionsData}
            onRestoreCancelled={
              // DELETE /lessons/:id/exceptions/:exceptionId requires scheduling.DELETE — gate on
              // the same permission or a write-only admin sees a button that 403s.
              !isTeacherView && can('scheduling', 'delete')
                ? (exceptionId) => restoreException.mutate({ lessonId: id || '', exceptionId })
                : undefined
            }
            restoringExceptionId={restoreException.isPending ? restoreException.variables?.exceptionId : undefined}
            // "Open weeks" affordance beside the Individual Weeks chips —
            // admin (scheduling.write) door for HybridLessonPattern.openClinicWeeks.
            onManageOpenClinicWeeks={
              lesson.HybridLessonPattern &&
              (lesson.HybridLessonPattern.individualWeeks?.length ?? 0) > 0 &&
              !lessonTermEnded &&
              can('scheduling', 'write')
                ? () => setOpenWeeksDialogOpen(true)
                : undefined
            }
            onNavigateToTermLesson={(lessonId) =>
              navigate(`${basePath}/classes/${lessonId}`)
            }
          />
        </Grid>

        {/* Enrollments Card */}
        <Grid item xs={12} md={6}>
          <EnrollmentCard
            enrollments={enrollments}
            capacity={capacity}
            isLoading={enrollmentsLoading}
            isTeacherView={isTeacherView}
            onEnrollClick={handleOpenEnrollModal}
            onUnenrollClick={handleUnenroll}
            onViewStudentClick={handleViewStudent}
            lesson={lesson ?? undefined}
          />
        </Grid>

        {/* Hybrid Pattern Card (if applicable) */}
        {lesson.HybridPattern && (
          <Grid item xs={12} md={6}>
            <HybridPatternCard
              pattern={lesson.HybridPattern}
              streamName={lesson.Stream?.name}
              streamSlotDuration={lesson.Stream?.individualSlotDuration}
            />
          </Grid>
        )}

        {/* Class Pieces Card */}
        <Grid item xs={12}>
          <LessonPiecesCard
            lessonId={lesson.id}
            instrumentId={lesson.instrumentId}
            programLevelId={lesson.programLevelId}
            isTeacherView={isTeacherView}
          />
        </Grid>

        {/* Class Notes Card */}
        <Grid item xs={12}>
          <LessonNotesCard
            notes={lessonNotes}
            isLoading={isLoadingNotes}
            onViewAllClick={() => setAllNotesModalOpen(true)}
          />
        </Grid>

        {/* Class Resources Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Class Resources
              </Typography>
              {isDriveConnected && canManageDriveFiles && (
                <Button
                  variant="contained"
                  onClick={() => setUploaderOpen(true)}
                >
                  Upload Resource
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Web links (YouTube etc.) — their own table, no Drive needed (2026-09-07). */}
            {id && (
              <ClassLinksSection
                lessonId={id}
                programLevel={lesson?.ProgramLevel ? { id: lesson.ProgramLevel.id, name: lesson.ProgramLevel.name } : null}
                editable
              />
            )}

            {!isDriveConnected ? (
              <Alert severity="info">
                Google Drive is not connected. Connect Google Drive in{' '}
                <Button
                  size="small"
                  onClick={() => navigate(`${isTeacherView ? '../' : ''}../google-drive`)}
                  sx={{ ml: 1 }}
                >
                  Settings
                </Button>
              </Alert>
            ) : (
              <>
                {/* Google Drive Folder Info */}
                {lessonFolderMapping ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudIcon sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Google Drive Folder
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {rootFolderName && (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {rootFolderName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">/</Typography>
                            </>
                          )}
                          <MuiLink
                            href={lessonFolderMapping.folderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {lessonFolderMapping.folderName}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </MuiLink>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SyncStatusBadge
                        status={lessonFolderMapping.syncStatus}
                        showLabel
                        size="small"
                      />
                      {lessonFolderMapping.lastSyncAt && (
                        <Typography variant="caption" color="text.secondary">
                          Last synced: {formatSyncTime(lessonFolderMapping.lastSyncAt)}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No Google Drive folder linked to this class.{' '}
                    {!isTeacherView && can('integrations', 'write') && (
                      <Button
                        size="small"
                        onClick={() => navigate('../google-drive')}
                      >
                        Link Folder
                      </Button>
                    )}
                  </Alert>
                )}

                <FileList
                  lessonId={id}
                  editable={canManageDriveFiles}
                  showFilters={false}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* File Uploader */}
      {isDriveConnected && uploaderOpen && (
        <Box sx={{ mt: 3 }}>
          <DriveFileUploader
            open={uploaderOpen}
            onClose={() => setUploaderOpen(false)}
            lessonId={id}
          />
        </Box>
      )}

      {/* Enroll Students Modal */}
      <EnrollStudentsDialog
        open={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        students={availableStudents}
        selectedStudents={selectedStudents}
        studentSearch={studentSearch}
        onSearchChange={setStudentSearch}
        onToggleStudent={handleToggleStudent}
        onEnroll={handleEnrollStudents}
        isLoading={isEnrollLoading}
        availableSpots={capacity?.available}
        lesson={lesson ?? undefined}
        term={lesson?.Term}
        startWeek={enrollStartWeek}
        onStartWeekChange={setEnrollStartWeek}
      />

      {/* Enrolled into a term the family was already invoiced for */}
      <AlreadyInvoicedDialog entries={alreadyInvoiced.entries} onClose={alreadyInvoiced.clear} />

      {/* Unenroll Confirmation */}
      <ConfirmDialog
        open={unenrollDialogOpen}
        title="Unenrol Student"
        message={`Are you sure you want to unenrol ${studentToUnenroll?.Student.firstName} ${studentToUnenroll?.Student.lastName} from this class?`}
        confirmLabel="Unenrol"
        confirmColor="error"
        loading={isUnenrollLoading}
        onConfirm={handleConfirmUnenroll}
        onCancel={() => setUnenrollDialogOpen(false)}
      />

      {/* View Student Modal */}
      <ViewStudentDialog
        open={viewStudentOpen}
        onClose={() => setViewStudentOpen(false)}
        student={selectedStudent}
        enrollments={studentEnrollments}
        isLoadingEnrollments={isLoadingStudentEnrollments}
      />

      {/* All Notes Modal */}
      <AllNotesDialog
        open={allNotesModalOpen}
        onClose={() => setAllNotesModalOpen(false)}
        lessonName={lesson?.name || ''}
        notes={lessonNotes}
      />

      {/* Assign Replacement Teacher Modal — acts on the CURRENT term's row for
          multi-term lessons (the page may be showing a past term). */}
      {currentTermLesson && (
        <AssignReplacementTeacherDialog
          open={replacementModalOpen}
          lesson={currentTermLesson}
          onClose={() => setReplacementModalOpen(false)}
        />
      )}

      {/* Cancel Class Modal */}
      <CancelClassDialog
        open={cancelModalOpen}
        lessonId={id || ''}
        onClose={() => setCancelModalOpen(false)}
      />

      {/* Reschedule Class Modal — acts on the CURRENT term's row for multi-term
          lessons (the page may be showing a past term). */}
      {currentTermLesson && (
        <RescheduleClassDialog
          open={rescheduleModalOpen}
          lesson={currentTermLesson}
          onClose={() => setRescheduleModalOpen(false)}
        />
      )}

      {/* Open-clinic-weeks management (launched from the Individual Weeks chips) */}
      {lesson.HybridLessonPattern && (
        <OpenClinicWeeksDialog
          open={openWeeksDialogOpen}
          onClose={() => setOpenWeeksDialogOpen(false)}
          lessonId={lesson.id}
          individualWeeks={lesson.HybridLessonPattern.individualWeeks}
          openClinicWeeks={lesson.HybridLessonPattern.openClinicWeeks ?? []}
          teacherName={
            lesson.Instructor?.User
              ? `${lesson.Instructor.User.firstName} ${lesson.Instructor.User.lastName}`
              : null
          }
          termName={lesson.Term?.name ?? null}
        />
      )}

      {/* Change Location Modal — same current-term resolution as Reschedule. */}
      {currentTermLesson && (
        <ChangeLocationDialog
          open={changeLocationModalOpen}
          lesson={currentTermLesson}
          onClose={() => setChangeLocationModalOpen(false)}
        />
      )}

      {/* Message Class Modal */}
      <ClassMessageModal
        open={messageModalOpen}
        lessonName={lesson.name}
        parents={classMembersData?.parents ?? []}
        students={classMembersData?.students ?? []}
        loading={classMembersLoading}
        loadError={classMembersError}
        sending={classBroadcastMutation.isPending}
        onClose={() => setMessageModalOpen(false)}
        onSend={handleSendClassMessage}
      />
    </Box>
  );
}
